import type { ProjectTask } from '@/frontend/features/Fde/types'

export interface TaskTreeRow {
  task: ProjectTask
  depth: number
  context: boolean
  hasChildren: boolean
}
export interface TaskListGroup {
  id: string
  label: string
  status?: ProjectTask['status']
  rows: TaskTreeRow[]
  count: number
}

// Filtering selects work; ancestors are retained only to explain where that
// work belongs. A child keeps its own status/category, even under another group.
export function taskListGroups(
  matches: ProjectTask[],
  allTasks: ProjectTask[],
  groupBy: 'status' | 'category',
  collapsed: Set<string>,
  labels: { statuses: Record<ProjectTask['status'], string>; no_category: string },
  statuses: ProjectTask['status'][],
): TaskListGroup[] {
  const byId = new Map(allTasks.map((task) => [task.id, task]))
  const matching = new Set(matches.map((task) => task.id))
  const included = new Set<string>()
  for (const task of matches) {
    let current: ProjectTask | undefined = task
    const path = new Set<string>()
    while (current && !path.has(current.id)) {
      included.add(current.id)
      path.add(current.id)
      current = current.parent_task_id ? byId.get(current.parent_task_id) : undefined
    }
  }
  const children = new Map<string, ProjectTask[]>()
  const roots: ProjectTask[] = []
  for (const task of allTasks.filter((task) => included.has(task.id))) {
    if (task.parent_task_id && included.has(task.parent_task_id)) {
      const siblings = children.get(task.parent_task_id) ?? []
      siblings.push(task)
      children.set(task.parent_task_id, siblings)
    } else roots.push(task)
  }
  const groups = new Map<string, TaskListGroup>()
  if (groupBy === 'status') {
    statuses.forEach((status) =>
      groups.set(status, {
        id: status,
        label: labels.statuses[status],
        status,
        rows: [],
        count: 0,
      }),
    )
  }
  const visited = new Set<string>()
  function appendRoot(root: ProjectTask) {
    const rows: TaskTreeRow[] = []
    const branch = new Set<string>()
    function walk(task: ProjectTask, depth: number, hidden: boolean) {
      if (visited.has(task.id)) return
      visited.add(task.id)
      branch.add(task.id)
      const descendants = children.get(task.id) ?? []
      if (!hidden)
        rows.push({
          task,
          depth,
          context: !matching.has(task.id),
          hasChildren: descendants.length > 0,
        })
      descendants.forEach((child) => walk(child, depth + 1, hidden || collapsed.has(task.id)))
    }
    walk(root, 0, false)
    // A filtered-out parent is context, not a reason to put matching open work
    // in the completed group. Anchor that branch to its first matching task.
    const anchor = matching.has(root.id)
      ? root
      : (matches.find((task) => branch.has(task.id)) ?? root)
    const id = groupBy === 'status' ? anchor.status : `category:${anchor.task_category_id ?? ''}`
    const group = groups.get(id) ?? {
      id,
      label: anchor.category?.name ?? labels.no_category,
      rows: [],
      count: 0,
    }
    group.rows.push(...rows)
    // Headers count top-level branches. Descendants retain independent states;
    // a completed child must not inflate its parent's "A fazer" count.
    group.count += 1
    groups.set(id, group)
  }
  roots.forEach(appendRoot)
  // Defensive recovery for incomplete or legacy cyclic payloads: never lose
  // matched work or recurse indefinitely if its ancestor is unavailable.
  matches.filter((task) => !visited.has(task.id)).forEach(appendRoot)
  return [...groups.values()].sort((a, b) =>
    groupBy === 'category' ? a.label.localeCompare(b.label) : 0,
  )
}

export function taskListDropTarget(
  group: TaskListGroup,
  task: ProjectTask,
  index: number,
  sameGroup: boolean,
) {
  const status = sameGroup ? task.status : (group.status ?? task.status)
  const rows = group.rows.filter((row) => row.task.id !== task.id)
  const next = rows.slice(index).find((row) => !row.context && row.task.status === status)
  return { status, beforeId: next?.task.id ?? null }
}
